import { FC, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'

import { Delete, EyeVisible, Pencil } from '@factorialco/f0-react/icons/app'
import { PayrollPolicyPeriodTypesPeriodTypeEnum } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { Result, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import AddToPaymentModal from 'modules/expenses/components/expenses/AddToPaymentModal'
import { MissingCategoriesModal } from 'modules/expenses/components/expenses/AddToPayrollModal/_MissingCategoriesModal'
import ReimburseModal from 'modules/expenses/components/expenses/AddToPayrollModal/_ReimburseModal'
import ReviewModal from 'modules/expenses/components/expenses/ReviewModal'
import DeleteGroupModal from 'modules/expenses/components/groups/DeleteModal'
import { useExpensesNavigation } from 'modules/expenses/hooks/useExpensesNavigation'
import {
  Group,
  useAddToPayroll,
  useApprove,
  useDelete,
  usePayWithSepa,
  useReject,
  useRemoveFromPayroll,
  useRestartReview,
  useRevertPaid,
  useSendForApproval,
  useSetToPaid,
} from 'modules/expenses/hooks/useGroup'
import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { ModalAction } from 'modules/expenses/lib/expensables/modalAction'
import { canEditGroup } from 'modules/expenses/lib/groups/availableActions'
import {
  canAddGroupToPayroll,
  canDeleteGroup,
  canPayGroupWithSepa,
  canRemoveGroupFromPayroll,
  canReviewGroup,
  canSendGroupForApproval,
} from 'modules/expenses/lib/groups/availableActions'
import { isDraftGroup, isRejectedGroup } from 'modules/expenses/lib/groups/group'
import {
  ExpenseGroupActions,
  PrimaryExpenseGroupAction,
  PrimaryExpenseGroupDropdownAction,
  SecondaryExpenseGroupAction,
} from 'modules/expenses/pages/my_expenses/Groups/DetailPage/usePageActions'

function showFlashMessage<Data>(
  handled: Result<Data>,
  {
    success,
    error,
  }: {
    success: string
    error: string
  }
) {
  if (handled.success) {
    app.setFlash({
      type: 'correct',
      message: success,
    })
  } else {
    app.setFlash({
      type: 'wrong',
      message: error,
    })
  }
}

interface ModalsProps {
  action: ModalAction | null
  anyAutomaticExpense: boolean
  anyMissingPayrollCategory: boolean
  currentPayrollPeriodOpen: boolean
  groupId: string
  onReject: (reason?: string) => void
  onGlobalApprove: () => void
  onAddToPayroll: (period: PayrollPolicyPeriodTypesPeriodTypeEnum) => void
  onAddToPayment: () => void
  onDelete: () => void
  onClose: () => void
}

interface UsePageActions {
  group?: Group
  currentPayrollPeriodOpen: boolean
  canSetToPaid: boolean
  canRestartApprovalFlow: boolean
  canOverrideApprovalFlow: boolean
}

interface UsePageActionsReturn {
  actions: ExpenseGroupActions
  Modals: FC
}

const Modals = ({
  action,
  anyAutomaticExpense,
  anyMissingPayrollCategory,
  currentPayrollPeriodOpen,
  groupId,
  onReject,
  onGlobalApprove,
  onAddToPayroll,
  onAddToPayment,
  onDelete,
  onClose,
}: ModalsProps) => (
  <>
    {action === ModalAction.Reject && (
      <ReviewModal
        type="reject"
        title={i18n.t('expenses_v2.group_detail.modals.reject.title')}
        onSubmit={onReject}
        onClose={onClose}
      />
    )}
    {action === ModalAction.GlobalApprove && (
      <ReviewModal
        type="approve"
        title={i18n.t('expenses_v2.list.actions.modal.title_global_approve')}
        description={i18n.t('expenses_v2.list.actions.modal.description_global_approve')}
        onSubmit={onGlobalApprove}
        onClose={onClose}
      />
    )}
    {action === ModalAction.CreatePayrollSupplement &&
      (anyMissingPayrollCategory ? (
        <MissingCategoriesModal onClose={onClose} />
      ) : (
        <ReimburseModal
          onSubmit={onAddToPayroll}
          onClose={onClose}
          currentPeriodOpen={currentPayrollPeriodOpen}
        />
      ))}
    {action === ModalAction.AddToPayment && (
      <AddToPaymentModal onClose={onClose} groupId={groupId} onSubmit={onAddToPayment} />
    )}
    {action === ModalAction.Delete && (
      <DeleteGroupModal
        anyAutomaticExpense={anyAutomaticExpense}
        onSubmit={onDelete}
        onClose={onClose}
      />
    )}
  </>
)

export const usePageActions = ({
  group,
  currentPayrollPeriodOpen,
  canSetToPaid,
  canRestartApprovalFlow,
  canOverrideApprovalFlow,
}: UsePageActions): UsePageActionsReturn => {
  const { employee, company } = useCurrentSession()
  const { canSyncWithPayroll, canSyncWithBanking } = useOutletContext<RouteContextParams>()
  const navigate = useNavigate()
  const { navigation } = useExpensesNavigation()

  const [action, setAction] = useState<ModalAction | null>(null)

  const { sendForApproval, loading: sendingForApproval } = useSendForApproval()
  const { approve, loading: approving } = useApprove()
  const { reject } = useReject()
  const { restartReview, loading: restartingReview } = useRestartReview()
  const { setToPaid, loading: settingToPaid } = useSetToPaid()
  const { revertPaid, loading: revertingPaid } = useRevertPaid()
  const { addToPayroll, loading: addingToPayroll } = useAddToPayroll()
  const { sendToPayWithSepa, loading: addingToSepa } = usePayWithSepa()
  const { removeFromPayroll, loading: removingFromPayroll } = useRemoveFromPayroll()
  const { delete: destroy, loading: deleting } = useDelete()

  if (!group) return { actions: { primary: undefined, secondary: [] }, Modals: () => null }

  const primary: (PrimaryExpenseGroupAction | PrimaryExpenseGroupDropdownAction)[] = []
  const secondary: SecondaryExpenseGroupAction[] = []

  const onSendForApproval = async () => {
    const handled = handleResult(await sendForApproval(group.id, company.id), () => null)
    showFlashMessage(handled, {
      success: i18n.t('expenses_v2.group_detail.flash_messages.send_for_approval.success'),
      error: i18n.t('expenses_v2.group_detail.flash_messages.send_for_approval.error'),
    })
  }

  const onApprove = async (globalApprove?: boolean) => {
    const handled = handleResult(await approve(group.id, company.id, globalApprove), () => null)
    showFlashMessage(handled, {
      success: i18n.t('expenses_v2.group_detail.flash_messages.approve.success'),
      error: i18n.t('expenses_v2.group_detail.flash_messages.approve.error'),
    })
  }

  const onReject = async (reason?: string) => {
    const handled = handleResult(await reject(group.id, company.id, reason), () => null)
    showFlashMessage(handled, {
      success: i18n.t('expenses_v2.group_detail.flash_messages.reject.success'),
      error: i18n.t('expenses_v2.group_detail.flash_messages.reject.error'),
    })
  }

  const onRestartReview = async () => {
    const handled = handleResult(await restartReview(group.id, company.id), () => null)
    showFlashMessage(handled, {
      success: i18n.t('expenses_v2.group_detail.flash_messages.restart_review.success'),
      error: i18n.t('expenses_v2.group_detail.flash_messages.restart_review.error'),
    })
  }

  const onSetToPaid = async () => {
    const handled = handleResult(await setToPaid(group.id, company.id), () => null)
    showFlashMessage(handled, {
      success: i18n.t('expenses_v2.group_detail.flash_messages.set_to_paid.success'),
      error: i18n.t('expenses_v2.group_detail.flash_messages.set_to_paid.error'),
    })
  }

  const onRevertPaid = async () => {
    const handled = handleResult(await revertPaid(group.id, company.id), () => null)
    showFlashMessage(handled, {
      success: i18n.t('expenses_v2.group_detail.flash_messages.revert_paid.success'),
      error: i18n.t('expenses_v2.group_detail.flash_messages.revert_paid.error'),
    })
  }

  const onAddToPayroll = async (period: PayrollPolicyPeriodTypesPeriodTypeEnum) => {
    const handled = handleResult(await addToPayroll(group.id, period, company.id), () => null)
    showFlashMessage(handled, {
      success: i18n.t('expenses_v2.group_detail.flash_messages.add_to_payroll.success'),
      error: i18n.t('expenses_v2.group_detail.flash_messages.add_to_payroll.error'),
    })
  }

  const onSendToPayWithSepa = async () => {
    const handled = handleResult(await sendToPayWithSepa(group.id, company.id), () => null)
    showFlashMessage(handled, {
      success: i18n.t('expenses_v2.group_detail.flash_messages.sent_to_pay_with_sepa.success'),
      error: i18n.t('expenses_v2.group_detail.flash_messages.sent_to_pay_with_sepa.error'),
    })
  }

  const onRemoveFromPayroll = async () => {
    const handled = handleResult(await removeFromPayroll(group.id, company.id), () => null)
    showFlashMessage(handled, {
      success: i18n.t('expenses_v2.group_detail.flash_messages.remove_from_payroll.success'),
      error: i18n.t('expenses_v2.group_detail.flash_messages.remove_from_payroll.error'),
    })
  }

  const onDelete = async () => {
    const handled = handleResult(await destroy(group.id, company.id), () => null)
    showFlashMessage(handled, {
      success: i18n.t('expenses_v2.group_detail.flash_messages.delete.success'),
      error: i18n.t('expenses_v2.group_detail.flash_messages.delete.error'),
    })
    if (handled.success) navigate(navigation.list.fullPath())
  }

  if (canSendGroupForApproval(group, employee)) {
    primary.push({
      label: i18n.t('expenses_v2.buttons.send_for_approval'),
      onClick: () => !sendingForApproval && onSendForApproval(),
    })
  }

  const approveActions: { label: string; value: string }[] = [
    ...(canReviewGroup(group)
      ? [{ label: i18n.t('expenses_v2.buttons.approve_group'), value: 'approve' }]
      : []),
    ...(canOverrideApprovalFlow && group.status === 'pending'
      ? [{ label: i18n.t('expenses_v2.buttons.global_approve'), value: 'global_approve' }]
      : []),
  ]

  if (approveActions.length > 1) {
    const value = approveActions[0]?.value ?? 'approve'
    primary.push({
      items: approveActions,
      value,
      onClick: (input_value) => {
        if (!approving && input_value === 'approve') onApprove(false)
        else if (!approving && input_value === 'global_approve') onApprove(true)
      },
    })
  } else if (approveActions.length === 1 && approveActions[0]) {
    primary.push({
      label: approveActions[0]?.label,
      onClick: () => !approving && onApprove(approveActions[0]?.value === 'global_approve'),
    })
  }

  if (canRemoveGroupFromPayroll(group, canSyncWithPayroll)) {
    primary.push({
      label: i18n.t('expenses_v2.buttons.remove_from_payroll'),
      onClick: () => !removingFromPayroll && onRemoveFromPayroll(),
    })
  }

  secondary.push({
    label: canEditGroup(group, employee?.id)
      ? i18n.t('expenses_v2.buttons.edit_group')
      : i18n.t('expenses_v2.buttons.group_details'),
    onClick: () => navigate(navigation.groups.id.edit.fullPath({ groupId: group.id })),
    icon: canEditGroup(group, employee?.id) ? Pencil : EyeVisible,
  })

  if (canSetToPaid && group.revertablePaid) {
    secondary.push({
      label: i18n.t('expenses_v2.buttons.revert_paid'),
      onClick: () => !revertingPaid && onRevertPaid(),
    })
  }

  if (group.settableAsPaid) {
    const items = [
      ...(canSetToPaid
        ? [
            {
              label: i18n.t('expenses_v2.buttons.set_as_paid'),
              value: 'set_as_paid',
            },
          ]
        : []),
      ...(canPayGroupWithSepa(group, canSyncWithBanking)
        ? [
            {
              label: i18n.t('expenses_v2.buttons.reimburse_via_sepa'),
              value: 'reimburse_via_sepa',
            },
          ]
        : []),
      ...(canAddGroupToPayroll(group, canSyncWithPayroll)
        ? [
            {
              label: i18n.t('expenses_v2.buttons.reimburse_through_payroll'),
              value: 'reimburse_through_payroll',
            },
          ]
        : []),
    ]
    const paidOnClick = (value: string) => {
      if (!settingToPaid && value === 'set_as_paid') onSetToPaid()
      else if (!addingToSepa && value === 'reimburse_via_sepa') setAction(ModalAction.AddToPayment)
      else if (!addingToPayroll && value === 'reimburse_through_payroll')
        setAction(ModalAction.CreatePayrollSupplement)
    }

    if (items.length > 1) {
      primary.push({
        items,
        value: items[0]?.value,
        onClick: paidOnClick,
      })
    } else if (items.length === 1 && items[0]) {
      primary.push({
        label: items[0]?.label,
        onClick: () => paidOnClick(items[0]?.value ?? 'set_as_paid'),
      })
    }
  }

  if (canDeleteGroup(group, employee?.id) && isRejectedGroup(group)) {
    secondary.push({
      label: i18n.t('expenses_v2.buttons.delete_group'),
      variant: 'critical',
      icon: Delete,
      onClick: () => setAction(ModalAction.Delete),
    })
  }

  if (canRestartApprovalFlow && group.restartable) {
    secondary.push({
      label: i18n.t('expenses_v2.buttons.reset_approval_flow'),
      onClick: () => !restartingReview && onRestartReview(),
    })
  }

  if (canDeleteGroup(group, employee?.id) && isDraftGroup(group)) {
    secondary.push({
      label: i18n.t('expenses_v2.buttons.delete_group'),
      variant: 'critical',
      icon: Delete,
      onClick: () => !deleting && setAction(ModalAction.Delete),
    })
  }

  if (canReviewGroup(group)) {
    secondary.push({
      label: i18n.t('expenses_v2.buttons.reject_group'),
      variant: 'critical',
      onClick: () => setAction(ModalAction.Reject),
    })
  }

  return {
    actions: { primary: primary[0], secondary },
    Modals: () => (
      <Modals
        action={action}
        currentPayrollPeriodOpen={currentPayrollPeriodOpen}
        anyAutomaticExpense={group.anyAutomaticExpense}
        anyMissingPayrollCategory={group.anyMissingPayrollCategory}
        onReject={onReject}
        groupId={group.id}
        onGlobalApprove={() => onApprove(true)}
        onAddToPayroll={onAddToPayroll}
        onAddToPayment={onSendToPayWithSepa}
        onDelete={onDelete}
        onClose={() => setAction(null)}
      />
    ),
  }
}
